// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList, SimpleChanges,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { LMModel, LMModelChangedEvent } from '@form/model';
import { LMFormState } from '@states/models/lm-form-state.model';
import { LMFormPersonalization } from '@states/models/lm-form-personalization.model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMContextService, LMLoggerService } from '@services/index';
import { LMColumnsUndistributedComponent } from './lm-columns-undistributed.component';
import { get } from 'lodash-es';
import { isEmpty } from 'lodash-es';

import { LMColumnsDistributedComponent } from './lm-columns-distributed.component';
import { SubscriptionLike as ISubscription } from 'rxjs';
import { LMFormLayoutService, LMFormService, LMFormSpecUtilService } from '@form/services';
import { LMFieldLayoutService } from '@field/services';
import { LMGlobalPersonalizationService } from '@states/services';

enum LMSectionLayoutType {
  FORM_REF = 0,
  NO_FORM_REF,
  BUTTON
}

enum LMSectionType {
  COLUMN = 0,
  HEADER,
  FIELD,
  TEXT,
  PARAGRAPH,
  CAPTCHA_CONTROL
}

enum LMSectionColumnLayoutInfoType {
  DISTRIBUTED_NO_FIRST = 0,
  UNDISTRIBUTED,
  IMAGE_IN_FIRST_COLUMN,
  IS_TABLE,
  IS_PERCENT,
  IS_GRID
}

@Component({
  selector:    'lm-form-layout',
  standalone: false,
  templateUrl: './lm-form-layout.component.html',
  styleUrls:   [ 'lm-form-layout.component.scss' ],
  providers:   [ LMFieldServiceAdapter, LMFieldLayoutService, LMFormLayoutService ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMFormLayoutComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  // formSpec doesn't always come in as a LMFormSpec, can sometimes be LMBusFormSpec
  @Input() formSpec;
  @Input() formOptions: LMFormOptions = null;
  @Input() layoutInfo: LMFormLayoutInfo = {};

  @Input() specFields: LMSpecFields;
  @Input() layout: LMFormLayout;
  @Input() model: LMModel;
  @Input() formState: LMFormState;
  @Input() placeHolder: ViewContainerRef;
  @Input() formPersonalization: LMFormPersonalization;
  @Input() activatedTabChanged: boolean;
  @Input() parentIsModal: boolean;

  @Output() dataViewLoaded: EventEmitter<LMDataView> = new EventEmitter<LMDataView>();
  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @ViewChildren(LMColumnsUndistributedComponent) lmColumnsUndistributed: QueryList<LMColumnsUndistributedComponent>;
  @ViewChildren(LMColumnsDistributedComponent) lmColumnsDistributed: QueryList<LMColumnsDistributedComponent>;

  @HostBinding('class.form-layout-compact') @Input() useCompactFormLayout: boolean;
  @HostBinding('class.lm-display-type-is-smartphone') displayTypeIsSmartPhone: boolean;
  @HostBinding('style.height') get hostHeight() {
    return this.isInDashboard() ? 'auto' : '100%';
  }

  public lmColumnLayoutInfo: LMColumnLayoutInfo = {
    isTable:               false,
    isExplicit:            false,
    distributed:           false,
    grid:                  false,
    independent:           false,
    isPercent:             false,
    imageInFirstColumn:    false,
    parentIsCompoundField: false,
    isInDashboard:         false,
    sectionInfo:           null
  };

  public sectionLayoutType;
  public sectionType;
  public sectionColumnLayoutType;
  public originalCompactFieldLayout: LMCompoundFieldLayout;
  public sectionInfo: LMFormSectionInfo;
  public isActionForm: boolean;

  private readonly instanceId: string;
  private subscriptionArray: Array<ISubscription> = [];

  constructor(
    private lmConditionUtilService: LMConditionUtilService,
    private lmLoggerService: LMLoggerService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmFormService: LMFormService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmContextService: LMContextService,
    private lmFormLayoutService: LMFormLayoutService
  ) {
    this.instanceId = this.lmLoggerService.instanceId;
    this.sectionLayoutType = LMSectionLayoutType;
    this.sectionType = LMSectionType;
    this.sectionColumnLayoutType = LMSectionColumnLayoutInfoType;
  }

  // events
  onDataViewLoaded(dataView: LMDataView) {
    this.dataViewLoaded.emit(dataView);
  }

  onFieldEvent(event) {
    this.fieldEvent.emit(event);
  }

  ngOnInit() {
    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.time({
        id: `LMFormLayout-${this.instanceId}`,
        description: 'TOTAL FORM LAYOUT',
        functionName: 'ngOnInit()',
        timingStart: 'LMFormLayoutComponent -> ngOnInit()',
        timingEnd: 'LMFormLayoutComponent -> ngAfterViewInit()',
        className: 'LMFormLayoutComponent'
      });
    }

    this.lmLoggerService.log([ this, this.instanceId, 'ngOnInit()' ]);

    this.useCompactFormLayout = this.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout;

    this.subscriptionArray.push(
      this.model.modelChanged.subscribe((modelChangedEvent: LMModelChangedEvent) => {

        // listening for a next / previous
        if (modelChangedEvent.eventType === 'set') {
          this.updateImplicitColumns();
          this.updateSectionInfo();
        }
      })
    );

    this.subscriptionArray.push(
      this.lmConditionUtilService.evaluateConditions.subscribe( () => this.updateImplicitColumns()),
      this.lmConditionUtilService.evaluateConditions.subscribe(() => this.updateSectionInfo())
    );

    this.preProcess();
    this.displayTypeIsSmartPhone = this.lmContextService.isSmartPhone();

    if(this.formOptions && this.formOptions.isAdHoc) {
      if(!this.layoutInfo) {
        this.layoutInfo = {
          isAdHoc: this.formOptions.isAdHoc
        };
      } else {
        this.layoutInfo.isAdHoc = this.formOptions.isAdHoc;
      }
    }

    this.lmFormLayoutService.calculatePreviousHeaderSiblings(this.layout.sections, 1);
  }

  // onChanges required for ad hoc search
  ngOnChanges(changes: SimpleChanges) {
    if (this.formOptions?.isAdHoc && changes.layout) {
      if (this.layout) {
        this.preProcess();
        this.lmFormLayoutService.calculatePreviousHeaderSiblings(this.layout.sections, 1);
      }
    }
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);

    // this needs to be last in this method
    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.timeEnd(`LMFormLayout-${this.instanceId}`);
    }
  }

  ngOnDestroy() {
    // clean up subscriptions
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.subscriptionArray = [];
  }

  /**
   * Check column/pargraph layouts for sectiosn that should be displayed using "Summary form" styles
   * Summary form section is a section that is essentially all text fields. Can also include buttons, links, and checkbox
   *
   * Check column/paragraph layouts for sections where all labels are hidden
   *
   *  Need to know which header is the first header in the layout spec
   *  Check if column layouts include headers
   */
  private preProcess() {
    let foundFirstHeader = false;
    let visibleFieldFound = false;
    const isContextForm = this.lmFormSpecUtilService.isContextForm(this.formSpec as LMFormSpec);
    const isContextFormOnSide = this.lmFormSpecUtilService.isContextFormOnSide(this.formSpec as LMFormSpec);
    const isReadOnlyForm = (this.formOptions && (this.formOptions.isReadOnlyForm || this.formOptions.isDashBoardContent));
    const isDashboardPanelForm = this.formOptions && this.formOptions.isDashBoardContent;
    const formHasNumberLabel = this.lmFormLayoutService.formHasNumberLabel(this.layout.sections, this.model);
    const disableFieldOptions = this.layout?.disableFieldOptions;

    this.originalCompactFieldLayout = this.lmFormSpecUtilService.originalCompactFieldLayout(this.formSpec);

    if (this.layout.sections) {
      const headerIndexes = [];
      let lastCollapsibleHeader: number;
      this.layout.sections.forEach((section: LMFormLayoutSectionUI, index: number) => {
        if (!!section.header) {
          if (section.header.collapsible) {headerIndexes.push(index);}
        }
      });

      if (headerIndexes.length > 0) {
        lastCollapsibleHeader = headerIndexes[headerIndexes.length - 1];
      }

      const sections = this.layout.sections.map((section: LMFormLayoutSectionUI, index: number) => {
        if (isEmpty(this.layoutInfo)) {
          this.layoutInfo = {
            isContextForm,
            isContextFormOnSide,
            isReadOnlyForm,
            isDashboardPanelForm,
            disableFieldOptions
          };
        }

        const lmConditionUtilService = this.lmConditionUtilService;
        const field = section[Object.keys(section)[0]];
        const fieldIsVisible = field ? lmConditionUtilService.isValid(field.visibility, this.model) : false;
        let columnsWithHeaders = false;

        // Check if there are any fields visible before header
        // If true then header will not be rendered as first header
        // Header will still get <hr> element in header component template
        if (!section.header && fieldIsVisible) {
          visibleFieldFound = true;
        }

        if (section.header) {

          // Mark as first header if there are no other visible fields before header
          // And if no other header has been found
          if (!foundFirstHeader && fieldIsVisible && !visibleFieldFound) {
            foundFirstHeader = true;
            section.header.isFirst = true;
          }

          if (index === lastCollapsibleHeader) {
            section.header.isLast = true;
          }
        }

        if (section.columns) {
          columnsWithHeaders = this.columnsWithHeaders(section.columns);
        }

        const percent = get(section, 'columns[0].percent') !== undefined;

        let sectionInfo = this.lmFormLayoutService.getSectionInfo(section, this.model);

        // Display sections that don't have number labels as if they did
        // This is used to help left align sections properly when there is a number label on the form
        if (!sectionInfo.sectionHasNumberLabel) {
          section.displayAsNumberLabelSection = formHasNumberLabel;
        }

        // Search forms aren't summary, and don't hide labels
        if (this.lmFormSpecUtilService.isSearchForm(this.formSpec)) {
          sectionInfo = { isSummaryFormSection: false, isNoLabelSection: false };
        }

        section.columnLayoutInfo = {
          isTable:              section.table,
          isExplicit:           section.explicit,
          distributed:          (section.distributed || columnsWithHeaders) && !percent,
          grid:                 section.grid,
          independent:          section.independent,
          isPercent:            percent,
          imageInFirstColumn:   get(section, 'columns[0].elements[0].field.displayImageAs') !== undefined,
          nbrColumns:           section.nbrColumns,
          sectionInfo,
          isInDashboard:        this.isInDashboard()
        };

        section.columnLayoutInfo.undistributed = !(
          section.columnLayoutInfo.distributed ||
          section.columnLayoutInfo.isPercent ||
          section.columnLayoutInfo.imageInFirstColumn ||
          section.columnLayoutInfo.isTable ||
          section.columnLayoutInfo.grid
        );

        section.paragraphLayoutInfo = {
          sectionInfo
        };

        this.layoutInfo.columnLayoutInfo = section.columnLayoutInfo;

        return section;
      });

      this.layout.sections = sections;

      this.layout.sections.map((section: LMFormLayoutSectionUI, i) => {
        section.index = i;
        if (section.button) {
          section.sectionLayout = this.sectionLayoutType.BUTTON;

        } else if (section.formRef) {
          section.sectionLayout = this.sectionLayoutType.FORM_REF;

        } else {
          section.sectionLayout = this.sectionLayoutType.NO_FORM_REF;

          if (section.type === 'column') {
            section.sectionType = this.sectionType.COLUMN;

            if (section.table) {
              section.sectionColumnInfoType = this.sectionColumnLayoutType.IS_TABLE;

            } else if (section.columnLayoutInfo.distributed && !section.columnLayoutInfo.grid && !section.columnLayoutInfo.imageInFirstColumn) {
              section.sectionColumnInfoType = this.sectionColumnLayoutType.DISTRIBUTED_NO_FIRST;

            } else if (section.columnLayoutInfo.undistributed && !section.columnLayoutInfo.grid) {
              section.sectionColumnInfoType = this.sectionColumnLayoutType.UNDISTRIBUTED;

            } else if (section.columnLayoutInfo.imageInFirstColumn && !section.columnLayoutInfo.grid) {
              section.sectionColumnInfoType = this.sectionColumnLayoutType.IMAGE_IN_FIRST_COLUMN;

            } else if (section.columnLayoutInfo.isPercent && !section.columnLayoutInfo.grid) {
              section.sectionColumnInfoType = this.sectionColumnLayoutType.IS_PERCENT;
            } else if (section.columnLayoutInfo.grid) {
              section.sectionColumnInfoType = this.sectionColumnLayoutType.IS_GRID;
            }

          } else if (section.header) {
            section.sectionType = this.sectionType.HEADER;

          } else if (section.field) {
            section.sectionType = this.sectionType.FIELD;

          } else if (section.text) {
            section.sectionType = this.sectionType.TEXT;

          } else if (section.paragraph) {
            section.sectionType = this.sectionType.PARAGRAPH;

          } else if (section.checkcontrol) {
            section.sectionType = this.sectionType.CHECK_CONTROL;

          } else if (section.captchaControl) {
            section.sectionType = this.sectionType.CAPTCHA_CONTROL;
          }
        }
      });
    }
  }

  // If columns contain headers then treat them as a distributed layout
  private columnsWithHeaders(columns: Array<LMFormLayoutColumn>): boolean {
    const headers = [];
    columns.forEach((column) => {
      column.elements.forEach((element: LMFormLayoutElement) => {
        if (element.header) {
          element.header.isColumnHeader = true;
          headers.push(element);
        }
      });
    });
    return headers.length > 0;
  }

  public trackByIndex(index: number, section: LMFormLayoutSectionUI): number {
    return section.index;
  }

  private updateSectionInfo(): void {
    if (this.layout.sections) {
      this.layout.sections.forEach((section: LMFormLayoutSectionUI) => {
        const sectionInfo = this.lmFormLayoutService.getSectionInfo(section, this.model);

        if (section?.columnLayoutInfo?.sectionInfo) {
          section.columnLayoutInfo.sectionInfo = sectionInfo;
        }

        if (section?.paragraphLayoutInfo?.sectionInfo) {
          section.paragraphLayoutInfo.sectionInfo = sectionInfo;
        }
      });
    }
  }

  private updateImplicitColumns() {
    if (this.lmColumnsUndistributed?.toArray().length > 0) {
      this.lmColumnsUndistributed.forEach(comp => comp.updateImplicitColumns());
    }

    if (this.lmColumnsDistributed?.toArray().length > 0) {
      this.lmColumnsDistributed.forEach(comp => comp.updateImplicitColumns());
    }
  }
  private isInDashboard(): boolean {
    return this.formOptions?.isDashBoardContent;
  }
}
